import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
} from "lucide-react";
import { isPast } from "date-fns";
import { formatTimestamp, parseDateOnly, timestampMs } from "@/lib/datetime";
import AddActionItemModal from "@/components/AddActionItemModal";
import { useActionItemsStore } from "@/stores/actionItemsStore";
import { usePatientsStore } from "@/stores/patientsStore";
import { ActionItemResponse } from "@/lib/types";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePersistedState } from "@/hooks/usePersistedState";
import {
  ActionItemStatusMenu,
  PriorityBadge,
  formatDueDate,
} from "@/components/actionItems/actionItemDisplay";

const DEFAULT_SORTING: SortingState = [{ id: "created_at", desc: true }];
const DEFAULT_VISIBILITY: VisibilityState = {};
const HIDEABLE_COLUMNS: { id: string; label: string }[] = [
  { id: "priority", label: "Priority" },
  { id: "patient", label: "Patient" },
  { id: "created_at", label: "Created" },
  { id: "due_date", label: "Due" },
  { id: "status", label: "Status" },
];

const PRIORITY_ORDER: Record<string, number> = { URGENT: 0, MEDIUM: 1, LOW: 2 };
const STATUS_ORDER: Record<string, number> = { pending: 0, in_progress: 1, completed: 2, cancelled: 3 };

const dueTime = (item: ActionItemResponse) =>
  parseDateOnly(item.due_date)?.getTime() ?? Number.POSITIVE_INFINITY;

type ColumnMeta = { className?: string } | undefined;

const ActionItemsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    actionItems,
    isLoading,
    error,
    fetchActionItemsForCurrentOrg,
    updateActionItemStatus,
  } = useActionItemsStore();
  const { patients, fetchPatientsForCurrentOrg } = usePatientsStore();

  // View state: survives navigation and reloads.
  const [statusFilter, setStatusFilter] = usePersistedState<string>("view:action-items:status", "active");
  const [priorityFilter, setPriorityFilter] = usePersistedState<string | null>("view:action-items:priority", null);
  const [sorting, setSorting] = usePersistedState<SortingState>("view:action-items:sorting", DEFAULT_SORTING);
  const [columnVisibility, setColumnVisibility] = usePersistedState<VisibilityState>(
    "view:action-items:columns",
    DEFAULT_VISIBILITY
  );
  const [currentPage, setCurrentPage] = usePersistedState("view:action-items:page", 1);
  const [pageSize, setPageSize] = usePersistedState("view:action-items:pageSize", 10);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);

  useEffect(() => {
    fetchActionItemsForCurrentOrg();
    fetchPatientsForCurrentOrg();
  }, [fetchActionItemsForCurrentOrg, fetchPatientsForCurrentOrg]);

  const getPatientName = React.useCallback(
    (item: ActionItemResponse) => {
      if (item.patient) return `${item.patient.first_name} ${item.patient.last_name}`;
      if (!item.patient_id) return "General";
      const patient = patients.find((p) => p.id === item.patient_id);
      return patient ? `${patient.first_name} ${patient.last_name}` : "Unknown";
    },
    [patients]
  );

  const handleStatusChange = async (itemId: string, status: string) => {
    await updateActionItemStatus(itemId, { status: status as any });
  };

  // A row opens the item itself; the item page links on to the patient and the call.
  const openItem = (item: ActionItemResponse) => navigate(`/action-items/${item.id}`);

  const openPatient = (item: ActionItemResponse) => {
    if (item.patient_id) navigate(`/patients/${item.patient_id}?tab=action-items`);
  };

  const filteredItems = useMemo(() => {
    let items = actionItems;
    if (statusFilter === "active") {
      items = items.filter((item) => item.status !== "completed" && item.status !== "cancelled");
    } else if (statusFilter && statusFilter !== "all") {
      items = items.filter((item) => item.status === statusFilter);
    }
    if (priorityFilter) {
      items = items.filter((item) => item.priority === priorityFilter);
    }
    return items;
  }, [actionItems, statusFilter, priorityFilter]);

  const columns = useMemo<ColumnDef<ActionItemResponse>[]>(() => {
    const sortableHeader = (label: string): ColumnDef<ActionItemResponse>["header"] =>
      ({ column }) => {
        const sorted = column.getIsSorted();
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8"
            onClick={() => column.toggleSorting(sorted === "asc")}
          >
            {label}
            {sorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : sorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
            )}
          </Button>
        );
      };

    return [
      {
        id: "complete",
        enableHiding: false,
        enableSorting: false,
        header: () => null,
        meta: { className: "w-[40px] pr-0" },
        cell: ({ row }) => (
          <Checkbox
            checked={row.original.status === "completed"}
            onCheckedChange={() =>
              handleStatusChange(
                row.original.id,
                row.original.status === "completed" ? "pending" : "completed"
              )
            }
            className="data-[state=checked]:border-success data-[state=checked]:bg-success"
          />
        ),
      },
      {
        id: "priority",
        accessorKey: "priority",
        header: sortableHeader("Priority"),
        meta: { className: "w-[100px]" },
        sortingFn: (a, b) =>
          (PRIORITY_ORDER[a.original.priority ?? ""] ?? 3) - (PRIORITY_ORDER[b.original.priority ?? ""] ?? 3),
        cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
      },
      {
        id: "title",
        accessorKey: "title",
        enableHiding: false,
        header: sortableHeader("Title"),
        sortingFn: (a, b) => a.original.title.localeCompare(b.original.title),
        cell: ({ row }) => (
          <>
            <div className={cn("font-medium", row.original.status === "completed" && "line-through text-muted-foreground")}>
              {row.original.title}
            </div>
            {row.original.description && (
              <div className="text-sm text-muted-foreground truncate max-w-md">{row.original.description}</div>
            )}
          </>
        ),
      },
      {
        id: "patient",
        accessorFn: (item) => getPatientName(item),
        header: sortableHeader("Patient"),
        meta: { className: "w-[180px]" },
        sortingFn: (a, b) => getPatientName(a.original).localeCompare(getPatientName(b.original)),
        cell: ({ row }) => <span className="text-sm">{getPatientName(row.original)}</span>,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: sortableHeader("Created"),
        meta: { className: "w-[120px]" },
        sortingFn: (a, b) =>
          timestampMs(a.original.created_at) - timestampMs(b.original.created_at),
        cell: ({ row }) => {
          const created = row.original.created_at;
          return (
            <span className="text-sm text-muted-foreground" title={formatTimestamp(created, "PPpp")}>
              {formatTimestamp(created, "MMM d")}
            </span>
          );
        },
      },
      {
        id: "due_date",
        accessorKey: "due_date",
        header: sortableHeader("Due"),
        meta: { className: "w-[120px]" },
        sortingFn: (a, b) => dueTime(a.original) - dueTime(b.original),
        cell: ({ row }) => {
          const dueInfo = formatDueDate(row.original.due_date);
          if (!dueInfo) return null;
          return (
            <span
              className={cn(
                "text-sm",
                dueInfo.isOverdue && "text-destructive font-medium",
                dueInfo.isUrgent && "text-warning font-medium"
              )}
            >
              {dueInfo.text}
            </span>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: sortableHeader("Status"),
        meta: { className: "w-[150px]" },
        sortingFn: (a, b) => (STATUS_ORDER[a.original.status] ?? 0) - (STATUS_ORDER[b.original.status] ?? 0),
        cell: ({ row }) => (
          <ActionItemStatusMenu
            status={row.original.status}
            onChange={(status) => handleStatusChange(row.original.id, status)}
          />
        ),
      },
      {
        id: "actions",
        enableHiding: false,
        enableSorting: false,
        header: () => null,
        meta: { className: "w-[50px]" },
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/action-items/${row.original.id}`)}>View Details</DropdownMenuItem>
              {row.original.patient_id && (
                <DropdownMenuItem onClick={() => openPatient(row.original)}>View Patient</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPatientName]);

  // If the sorted column has been hidden, fall back to the default sort.
  const effectiveSorting = useMemo(
    () => (sorting.some((s) => columnVisibility[s.id] === false) ? DEFAULT_SORTING : sorting),
    [sorting, columnVisibility]
  );

  const table = useReactTable({
    data: filteredItems,
    columns,
    state: { sorting: effectiveSorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const sortedRows = table.getRowModel().rows;
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const pageRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    if (safePage !== currentPage) setCurrentPage(safePage);
  }, [safePage, currentPage, setCurrentPage]);

  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const isDefaultView =
    statusFilter === "active" &&
    !priorityFilter &&
    JSON.stringify(sorting) === JSON.stringify(DEFAULT_SORTING) &&
    Object.values(columnVisibility).every((v) => v !== false);

  const resetView = () => {
    setStatusFilter("active");
    setPriorityFilter(null);
    setSorting(DEFAULT_SORTING);
    setColumnVisibility(DEFAULT_VISIBILITY);
    setCurrentPage(1);
  };

  const stats = useMemo(() => {
    const pending = actionItems.filter((i) => i.status === "pending").length;
    const inProgress = actionItems.filter((i) => i.status === "in_progress").length;
    const overdue = actionItems.filter((i) => {
      if (!i.due_date || i.status === "completed" || i.status === "cancelled") return false;
      const due = parseDateOnly(i.due_date);
      return due ? isPast(due) : false;
    }).length;
    return { pending, inProgress, overdue, total: actionItems.length };
  }, [actionItems]);

  if (isLoading && actionItems.length === 0) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error && actionItems.length === 0) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-8">
        <div className="py-8 text-center text-destructive">
          Error loading action items. Please try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-7xl min-w-0 space-y-4 p-4 md:space-y-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Action Items</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              <span>{stats.total} total</span>
              <span className="text-muted-foreground/50">•</span>
              <span>{stats.pending + stats.inProgress} active</span>
              {stats.overdue > 0 && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <span className="text-destructive">{stats.overdue} overdue</span>
                </>
              )}
            </div>
          </div>
          <Button onClick={() => setIsAddModalOpen(true)} className="w-full md:w-auto">
            <Plus className="mr-2 h-4 w-4" /> New Item
          </Button>
        </div>

        {/* Filters + view controls */}
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="h-9 w-[calc(50%-0.25rem)] md:w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={priorityFilter || "all"}
            onValueChange={(v) => {
              setPriorityFilter(v === "all" ? null : v);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="h-9 w-[calc(50%-0.25rem)] md:w-[130px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="URGENT">Urgent</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="LOW">Low</SelectItem>
            </SelectContent>
          </Select>

          {!isDefaultView && (
            <Button variant="ghost" size="sm" onClick={resetView} className="h-9 px-2 text-muted-foreground">
              Reset
            </Button>
          )}

          <div className="ml-auto hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {HIDEABLE_COLUMNS.map(({ id, label }) => {
                  const column = table.getColumn(id);
                  if (!column) return null;
                  return (
                    <DropdownMenuCheckboxItem
                      key={id}
                      checked={column.getIsVisible()}
                      onCheckedChange={(checked) => column.toggleVisibility(!!checked)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {label}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Phone: card list */}
        <div className="md:hidden overflow-hidden rounded-xl border bg-card">
          {pageRows.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-center text-muted-foreground">
              No action items found
            </div>
          ) : (
            <div className="divide-y">
              {pageRows.map((row) => {
                const item = row.original;
                const dueInfo = formatDueDate(item.due_date);
                const created = item.created_at;
                return (
                  <div
                    key={row.id}
                    className="flex items-start gap-3 p-3 cursor-pointer"
                    onClick={() => openItem(item)}
                  >
                    <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={item.status === "completed"}
                        onCheckedChange={() =>
                          handleStatusChange(item.id, item.status === "completed" ? "pending" : "completed")
                        }
                        className="data-[state=checked]:border-success data-[state=checked]:bg-success"
                      />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div
                            className={cn(
                              "font-medium break-words",
                              item.status === "completed" && "line-through text-muted-foreground"
                            )}
                          >
                            {item.title}
                          </div>
                          {item.description && (
                            <div className="text-sm text-muted-foreground line-clamp-2 break-words">
                              {item.description}
                            </div>
                          )}
                        </div>
                        <div onClick={(e) => e.stopPropagation()} className="shrink-0 -mr-2 -mt-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/action-items/${item.id}`)}>
                                View Details
                              </DropdownMenuItem>
                              {item.patient_id && (
                                <DropdownMenuItem onClick={() => openPatient(item)}>View Patient</DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="truncate max-w-[60%]">{getPatientName(item)}</span>
                        <span title={formatTimestamp(created, "PPpp")}>Created {formatTimestamp(created, "MMM d")}</span>
                        {dueInfo && (
                          <span
                            className={cn(
                              dueInfo.isOverdue && "text-destructive font-medium",
                              dueInfo.isUrgent && "text-warning font-medium"
                            )}
                          >
                            Due {dueInfo.text}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <PriorityBadge priority={item.priority} />
                        <ActionItemStatusMenu
                          status={item.status}
                          onChange={(status) => handleStatusChange(item.id, status)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {sortedRows.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t px-3 py-3">
              <span className="text-sm text-muted-foreground">
                {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, sortedRows.length)} of{" "}
                {sortedRows.length}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Tablet / desktop: table */}
        <div className="hidden md:block overflow-hidden rounded-xl border bg-card">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className={(header.column.columnDef.meta as ColumnMeta)?.className}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnCount} className="h-32 text-center text-muted-foreground">
                    No action items found
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => openItem(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const interactive = ["complete", "status", "actions"].includes(cell.column.id);
                      return (
                        <TableCell
                          key={cell.id}
                          className={(cell.column.columnDef.meta as ColumnMeta)?.className}
                          onClick={interactive ? (e) => e.stopPropagation() : undefined}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {sortedRows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, sortedRows.length)} of{" "}
                  {sortedRows.length}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddActionItemModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} />
    </>
  );
};

export default ActionItemsPage;
